import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  styled,
  SvgIcon,
  SvgIconProps,
  useTheme,
} from "@mui/material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import React, { ReactNode, useState } from "react";

import { useBoundingBoxesContext } from "context/boundingBoxesContext";
import { useImageSettingsContext } from "context/ImageSettingsContext/imageSettingsContext";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useSaveAnnotations } from "hooks/useSaveAnnotations/useSaveAnnotations";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";
import { setIsUnsaveModalOpen, unsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { DetectorModes } from "types/createDetector";
import { ImageQuery, Posicheck } from "types/imageQueries";
import { mapDeleteLabels } from "utils/ReviewPageUtils";

import { Point, Size } from "../types";

import { CanvasToolbarExtraSettings } from "./CanvasToolbarExtraSettings";
import { StepNavigation } from "./StepsNavigator";
import { ToolbarButton } from "./ToolbarButton";

function ShortcutItem({ commandName, shortcutKeys }: { commandName: string; shortcutKeys: ReactNode[] }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "calc(50% - 35px)" }}>
      <Typography fontWeight="medium">
        {commandName}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {shortcutKeys.map((shortcut, index) => (
          <>
            <Box
              sx={{
                border: "1px solid #006DD9",
                color: "#006DD9",
                backgroundColor: "#CCEAFF",
                borderRadius: "6px",
                padding: "4px 8px",
                fontSize: "14px",
                fontWeight: 500,
                alignSelf: "center",
              }}
            >
              {shortcut}
            </Box>
            {index !== shortcutKeys.length - 1 && <Typography fontWeight="medium">Or</Typography>}
          </>
        ))}
      </Box>
    </Box>
  );
}

const TRACKPAD_SHORTCUTS = [
  {
    commandName: "Zoom",
    shortcutKeys: ["Pinch gesture", "Ctrl + Two fingers gesture"],
  },
  {
    commandName: "Pan",
    shortcutKeys: ["Two fingers gesture"],
  },
];

const SCROLL_WHEEL_SHORTCUTS = [
  {
    commandName: "Zoom",
    shortcutKeys: ["Ctrl + Scroll wheel"],
  },
  {
    commandName: "Pan (Vertical)",
    shortcutKeys: ["Scroll wheel"],
  },
  {
    commandName: "Pan (Horizontal)",
    shortcutKeys: ["Shift + Scroll wheel"],
  },
  {
    commandName: "Drag on zoom",
    shortcutKeys: ["Middle click"],
  },
];

const CLICK_SHORTCUTS = [
  {
    commandName: "Create/Select annotation",
    shortcutKeys: ["Left click"],
  },
  {
    commandName: "Drag on zoom",
    shortcutKeys: ["Right click", "Ctrl + Left click"],
  },
  {
    commandName: "Delete annotation",
    shortcutKeys: ["Backspace", "Delete"],
  },
];

const SHORTCUT_SECTIONS = [
  {
    title: "Trackpad",
    shortcuts: TRACKPAD_SHORTCUTS,
  },
  {
    title: "Scroll wheel",
    shortcuts: SCROLL_WHEEL_SHORTCUTS,
  },
  {
    title: "Clicks",
    shortcuts: CLICK_SHORTCUTS,
  },
];

function DeleteIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3 18C2.45 18 1.97917 17.8042 1.5875 17.4125C1.19583 17.0208 1 16.55 1 16V3H0V1H5V0H11V1H16V3H15V16C15 16.55 14.8042 17.0208 14.4125 17.4125C14.0208 17.8042 13.55 18 13 18H3ZM13 3H3V16H13V3ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
          fill="#DBDFE3"
        />
      </svg>
    </SvgIcon>
  );
}

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  () => ({
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&::before": {
      display: "none",
    },
  })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => <MuiAccordionSummary {...props} />)(() => ({
  display: "flex",
  padding: "0.5rem 0.8rem",
  "& .MuiAccordionSummary-content": {
    justifyContent: "space-between",
    alignItems: "center",
    margin: 0,
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: "0.5rem 0.8rem",
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

interface CanvasToolbarProps {
  enableEdit?: boolean;
  imageQuery?: ImageQuery | Posicheck;
  setEnableEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  saveAnnotationCallback?: () => void;
  mode?: DetectorModes;
  title?: string;
  boxContainerSize: Size;
  position: Point;
  setPosition: (position: Point) => void;
}

export function CanvasToolbar(props: CanvasToolbarProps) {
  const {
    enableEdit,
    imageQuery,
    setEnableEdit,
    saveAnnotationCallback,
    mode,
    title,
    position,
    setPosition,
    boxContainerSize,
  } = props;

  const theme = useTheme();

  const dispatch = useAppDispatch();
  const unsavedAnnotation = useAppSelector(unsavedAnnotations);

  const {
    validBoxLabels,
    annotations,
    setSelectedAnnotation,
    setStepsData,
    setValidBoxLabels,
    stepsData,
    setAnnotations,
    fullScreen,
    setFullScreen,
    isSettingsExpanded,
    setIsSettingsExpanded,
    showAnnotations,
    setShowAnnotations,
    isBoundingBoxes,
  } = useBoundingBoxesContext();
  const hasBoundingBoxes = annotations?.length > 0;

  const { currentStep, totalSteps } = stepsData;
  const { isEditing, zoom, setZoom } = useImageSettingsContext();

  const { loadingAddLabel, saveAnnotations, mappedLabels } = useSaveAnnotations(
    annotations,
    validBoxLabels,
    stepsData,
    // Only pass the image query if it is an image query, not a posicheck
    imageQuery && "iq_id" in imageQuery ? imageQuery : undefined
  );

  const [openShortcutsDialog, setOpenShortcutsDialog] = useState<boolean>(false);

  const handleOpenShortcutsDialog = () => {
    setOpenShortcutsDialog(true);
  };

  const handleCloseShortcutsDialog = () => {
    setOpenShortcutsDialog(false);
  };

  const handleChangeExpanded = () => setIsSettingsExpanded(!isSettingsExpanded);

  const onVisibilityChange = () => {
    setShowAnnotations((prev) => !prev);
    setSelectedAnnotation(undefined);
  };

  const onEditHandle = (enableEdit: boolean) => {
    if (setEnableEdit) {
      if (unsavedAnnotation) {
        dispatch(setIsUnsaveModalOpen(true));
        return;
      }
      if (!enableEdit) {
        setEnableEdit((prev: boolean) => !prev);
        setStepsData((data) => ({ ...data, currentStep: data.totalSteps }));
      } else {
        setEnableEdit((prev: boolean) => !prev);
        setStepsData((data) => ({ ...data, currentStep: 1 }));
      }
    }
  };
  // This function is called when the user clicks on the save button.
  // It maps the annotations to the validBoxLabels and saves the labels.
  const onSave = async () => {
    if (!imageQuery) return;

    setValidBoxLabels(mappedLabels);
    let label;
    if (mode === DetectorModes.COUNT) {
      // For count the label is calculated in base to the length of the annotations
      label = annotations.length.toString();
    }

    await saveAnnotations(label).then(() => {
      // This callback is used to update the image query in the parent component to avoid a new api call.
      if (saveAnnotationCallback) saveAnnotationCallback();
    });
    if (setEnableEdit) {
      setEnableEdit(false);
    }
    setSelectedAnnotation(undefined);
    setStepsData((data) => ({ ...data, currentStep: data.totalSteps }));
  };

  const onDeleteCurrentAnnotations = () => {
    const updatedAnnotations = mapDeleteLabels(annotations, validBoxLabels, stepsData);
    setValidBoxLabels(updatedAnnotations);
  };

  const commonClasses = {
    size: "small",
    className: "flex items-center justify-center transition duration-150 ease-in-out h-[30px] w-[30px]",
  };

  const cursor = "default !important";

  return (
    <Box
      sx={{
        cursor,
        zIndex: 1,
      }}
    >
      <Accordion
        disableGutters
        expanded={isSettingsExpanded}
        className="bg-[#E5E9ED] w-full"
        sx={{
          borderRadius: "10px 10px 0 0",
          boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.1)",
          cursor,
          "& .MuiAccordionSummary-root": {
            padding: "10px",
            cursor,
          },
          "&:hover": {
            cursor,
          },
          "& .MuiAccordionSummary-root:hover": {
            cursor,
          },
        }}
      >
        <AccordionSummary
          sx={{
            cursor,
          }}
          disableRipple
          disableTouchRipple
        >
          <Box
            className="flex"
            sx={{
              flex: 1,
            }}
          >
            <Stack direction="row" spacing="10px">
              <ToolbarButton
                id="canvas-tool-bar-settings-button"
                data-testid="canvas-tool-bar-settings-button"
                {...commonClasses}
                title="Adjust Image Settings."
                onClick={handleChangeExpanded}
                color={theme.palette.common.white}
                active={isSettingsExpanded}
              >
                <TuneIcon />
              </ToolbarButton>
              {isBoundingBoxes && (
                <ToolbarButton
                  id="canvas-tool-bar-bboxes-visibility-button"
                  data-testid="canvas-tool-bar-bboxes-visibility-button"
                  {...commonClasses}
                  title="Toggle annotations/bounding boxes visibility."
                  onClick={onVisibilityChange}
                  color={theme.palette.common.white}
                  active={showAnnotations}
                >
                  {showAnnotations ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </ToolbarButton>
              )}
              {isEditing && isBoundingBoxes && hasBoundingBoxes && stepsData.currentStep <= validBoxLabels.length && (
                <Button
                  id="canvas-tool-bar-clear-all-annotations-button"
                  data-testid="canvas-tool-bar-clear-all-annotations-button"
                  sx={{
                    backgroundColor: "#49494D",
                    color: "white",
                    height: "30px",
                    paddingX: "5px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#808386",
                    },
                  }}
                  endIcon={<DeleteIcon sx={{ color: "white" }} />}
                  onClick={() => {
                    setAnnotations([]);
                  }}
                >
                  Clear all
                </Button>
              )}
            </Stack>
            {validBoxLabels.length > 0 && setEnableEdit && (
              <ToolbarButton
                id="canvas-tool-bar-edit-annotations-button"
                data-testid="canvas-tool-bar-edit-annotations-button"
                {...commonClasses}
                title="Create/Edit annotations."
                onClick={() => onEditHandle(!enableEdit)}
                color={theme.palette.common.white}
                marginLeft={0.5}
              >
                <EditIcon />
              </ToolbarButton>
            )}

            {validBoxLabels.length > 0 &&
              enableEdit &&
              (!loadingAddLabel ? (
                <>
                  <StepNavigation />
                  {currentStep <= totalSteps && (
                    <ToolbarButton
                      id="canvas-tool-bar-delete-current-annotations-button"
                      data-testid="canvas-tool-bar-delete-current-annotations-button"
                      {...commonClasses}
                      title="Delete Current Annotations"
                      onClick={onDeleteCurrentAnnotations}
                      color={theme.palette.common.white}
                      marginLeft={0.5}
                      textLabel={`Delete All ${validBoxLabels[currentStep - 1]?.label}`}
                    />
                  )}
                  <ToolbarButton
                    id="canvas-tool-bar-save-changes-button"
                    data-testid="canvas-tool-bar-save-changes-button"
                    {...commonClasses}
                    title="Save Changes"
                    onClick={onSave}
                    color={theme.palette.common.white}
                    marginLeft={0.5}
                    textLabel="Save New Label"
                  />
                </>
              ) : (
                <Box className="flex justify-center items-center ml-3">
                  <CircularProgress size={22} />
                </Box>
              ))}
          </Box>

          {title && (
            <Box
              sx={{
                flex: 1,
              }}
            >
              <Typography
                fontWeight="medium"
                align="center"
                sx={{
                  color: "#49494D",
                }}
              >
                {title}
              </Typography>
            </Box>
          )}

          <Stack
            direction="row"
            spacing="10px"
            alignItems="center"
            sx={{
              flex: 1,
              justifyContent: "end",
            }}
          >
            <Box
              sx={{ cursor: "pointer", color: "#919599", "&:hover": { color: "#49494D" } }}
              onClick={handleOpenShortcutsDialog}
            >
              <Typography fontWeight="medium">
                View shortcuts
              </Typography>
            </Box>
            <Modal
              size="medium"
              title="Canvas shortcuts"
              titleAlignment="left"
              open={openShortcutsDialog}
              onClose={handleCloseShortcutsDialog}
              body={
                <Stack
                  sx={{ width: "100%", height: "100%", justifyContent: "center", gap: "40px", padding: "20px 0px" }}
                >
                  {SHORTCUT_SECTIONS.map((section) => (
                    <Stack key={section.title} sx={{ width: "100%", gap: "10px" }}>
                      <Stack sx={{ gap: "10px" }}>
                        <Typography variant="title">
                          {section.title}
                        </Typography>
                        <Divider />
                      </Stack>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px 70px" }}>
                        {section.shortcuts.map((shortcut) => (
                          <ShortcutItem key={shortcut.commandName} {...shortcut} />
                        ))}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              }
            />
            <ToolbarButton
              id="canvas-tool-bar-zoom-out-button"
              data-testid="canvas-tool-bar-zoom-out-button"
              {...commonClasses}
              title="Zoom out (Ctrl+Scroll wheel)"
              onClick={() => {
                const centerPoint = {
                  x: boxContainerSize.width / 2,
                  y: boxContainerSize.height / 2,
                };

                const newZoom = Math.max(1, zoom / 2);

                // Calculate interpolation factor based on zoom level (more aggressive at lower zoom)
                const interpolation = Math.min(1, newZoom / 8);

                const newPosition = {
                  x: position.x * interpolation + centerPoint.x * (1 - interpolation),
                  y: position.y * interpolation + centerPoint.y * (1 - interpolation),
                };

                setPosition(newPosition);
                setZoom(newZoom);
              }}
              color={theme.palette.common.white}
              disabled={zoom <= 1}
            >
              <ZoomOutIcon />
            </ToolbarButton>

            <ToolbarButton
              id="canvas-tool-bar-zoom-in-button"
              data-testid="canvas-tool-bar-zoom-in-button"
              {...commonClasses}
              title="Zoom in (Ctrl+Scroll wheel)"
              onClick={() => {
                setZoom(Math.min((zoom ?? 0) * 2, 8));
              }}
              color={theme.palette.common.white}
              disabled={zoom >= 8}
            >
              <ZoomInIcon />
            </ToolbarButton>

            <ToolbarButton
              id="canvas-tool-bar-full-screen-button"
              data-testid="canvas-tool-bar-full-screen-button"
              {...commonClasses}
              title={fullScreen ? "Close expanded view." : "Open image in expanded view."}
              onClick={() => {
                setFullScreen(!fullScreen);
                setZoom(1);
              }}
              color={theme.palette.common.white}
            >
              {!fullScreen ? <OpenInNewIcon /> : <CloseFullscreenIcon />}
            </ToolbarButton>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <CanvasToolbarExtraSettings />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
